import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {jobKeys} from '../jobs/query-keys'
import {projectKeys} from '../projects/query-keys'
import {createInvoice, getInvoice, listInvoices, recordExternalInvoice, voidInvoice} from './api'
import {invoiceKeys} from './query-keys'
import type {CreateInvoiceInput, RecordExternalInvoiceInput} from './schemas'

export function useInvoices(options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: invoiceKeys.list(),
        queryFn: listInvoices,
        enabled: options?.enabled ?? true,
    })
}

export function useInvoice(id: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: invoiceKeys.detail(id),
        queryFn: () => getInvoice(id),
        enabled: options?.enabled ?? true,
    })
}

export function useCreateInvoice() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateInvoiceInput) => createInvoice(input),
        onSuccess: (invoice) => {
            void queryClient.invalidateQueries({queryKey: invoiceKeys.list()})
            void queryClient.invalidateQueries({queryKey: invoiceKeys.detail(invoice.id)})
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(invoice.projectId)})
            void queryClient.invalidateQueries({
                queryKey: projectKeys.billableLineItems(invoice.projectId),
            })
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
        },
    })
}

export function useRecordExternalInvoice(invoiceId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: RecordExternalInvoiceInput) => recordExternalInvoice(invoiceId, input),
        onSuccess: (invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice)
            void queryClient.invalidateQueries({queryKey: invoiceKeys.list()})
        },
    })
}

export function useVoidInvoice(invoiceId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => voidInvoice(invoiceId),
        onSuccess: (invoice) => {
            queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice)
            void queryClient.invalidateQueries({queryKey: invoiceKeys.list()})
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(invoice.projectId)})
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
        },
    })
}
